import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, ChevronRight, ChevronUp } from "lucide-react";
import { api } from "../lib/api";
import LogoImage from "../components/LogoImage";
import { ProductBadges } from "../components/Badges";
import { useUpvotes, getLocalCount, bumpLocalCount } from "../lib/upvotes";

function ShareButtons({ product }) {
    const url = `${window.location.origin}/product/${product.slug}`;
    const text = `Check out ${product.name} — a verified Indian alternative on BharatStack 🇮🇳: ${url}`;
    const enc = encodeURIComponent;
    const links = [
        {
            label: "WhatsApp",
            href: `https://wa.me/?text=${enc(text)}`,
            color: "bg-indiagreen text-white hover:bg-indiagreen-700",
            testid: "share-whatsapp",
        },
        {
            label: "X / Twitter",
            href: `https://twitter.com/intent/tweet?text=${enc(text)}`,
            color: "bg-ink text-white hover:bg-ink/90",
            testid: "share-twitter",
        },
        {
            label: "LinkedIn",
            href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`,
            color: "bg-[#0A66C2] text-white hover:bg-[#0a5ab3]",
            testid: "share-linkedin",
        },
    ];
    return (
        <div className="grid grid-cols-1 gap-2">
            {links.map((l) => (
                <a
                    key={l.label}
                    href={l.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-center text-sm font-semibold rounded-lg px-4 py-2.5 transition-colors ${l.color}`}
                    data-testid={l.testid}
                >
                    Share on {l.label}
                </a>
            ))}
        </div>
    );
}

export default function ProductDetail() {
    const { slug } = useParams();
    const [product, setProduct] = useState(null);
    const [siblings, setSiblings] = useState([]);
    const upv = useUpvotes();
    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setNotFound(false);
        api.getProduct(slug)
            .then((p) => {
                if (cancelled) return;
                setProduct(p);
                setCount(p.upvotes + getLocalCount(p.slug));
                document.title = `${p.name} — Indian alternative on BharatStack`;
                return api.getCategoryProducts(p.category_slug, { sort: "upvotes" });
            })
            .then((list) => {
                if (cancelled || !list) return;
                setSiblings(list.filter((x) => x.slug !== slug).slice(0, 4));
            })
            .catch(() => !cancelled && setNotFound(true))
            .finally(() => !cancelled && setLoading(false));
        return () => {
            cancelled = true;
        };
    }, [slug]);

    if (loading) {
        return (
            <div className="max-w-7xl mx-auto px-4 lg:px-6 py-20 text-center text-ink-muted">
                Loading product...
            </div>
        );
    }
    if (notFound || !product) {
        return (
            <div className="max-w-7xl mx-auto px-4 lg:px-6 py-20 text-center">
                <h1 className="font-heading font-bold text-3xl text-ink">
                    Product not found
                </h1>
                <Link
                    to="/categories"
                    className="text-saffron font-semibold mt-4 inline-block"
                >
                    Browse categories →
                </Link>
            </div>
        );
    }

    const hasVoted = upv.has(product.slug);
    const handleVote = () => {
        if (hasVoted) return;
        upv.add(product.slug);
        const localPrev = getLocalCount(product.slug);
        const next = bumpLocalCount(product.slug, localPrev);
        setCount(product.upvotes + next);
    };

    return (
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-10" data-testid="product-detail-page">
            <nav
                className="flex items-center gap-1 text-sm text-ink-muted mb-6 flex-wrap"
                aria-label="Breadcrumb"
            >
                <Link to="/" className="hover:text-saffron">
                    Home
                </Link>
                <ChevronRight className="h-3.5 w-3.5" />
                <Link to="/categories" className="hover:text-saffron">
                    Categories
                </Link>
                <ChevronRight className="h-3.5 w-3.5" />
                <Link
                    to={`/category/${product.category_slug}`}
                    className="hover:text-saffron"
                >
                    {product.category_name}
                </Link>
                <ChevronRight className="h-3.5 w-3.5" />
                <span className="text-ink font-medium">{product.name}</span>
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                {/* Main */}
                <div className="lg:col-span-8">
                    <div className="flex items-start gap-5">
                        <LogoImage
                            src={product.logo_url}
                            domain={product.domain}
                            name={product.name}
                            size={96}
                            rounded="rounded-2xl"
                        />
                        <div className="flex-1 min-w-0">
                            <Link
                                to={`/category/${product.category_slug}`}
                                className="text-xs font-semibold uppercase tracking-widest text-saffron-700 bg-saffron-50 border border-saffron-200 rounded-full px-2.5 py-0.5 inline-block"
                            >
                                {product.category_name}
                            </Link>
                            <h1 className="mt-3 font-heading font-extrabold text-3xl sm:text-4xl tracking-tight text-ink">
                                {product.name}
                            </h1>
                            <a
                                href={product.website_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-2 inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 hover:underline break-all font-medium"
                                data-testid="product-detail-url"
                            >
                                🌐 {product.website_url}
                            </a>
                            <div className="mt-3">
                                <ProductBadges product={product} />
                            </div>
                            {product.why_indian && (
                                <div
                                    className="mt-4 bg-saffron-50 border border-saffron/30 rounded-xl px-4 py-3 max-w-xl"
                                    data-testid="why-indian-box"
                                >
                                    <div className="flex items-start gap-2">
                                        <span aria-hidden className="text-lg">
                                            🇮🇳
                                        </span>
                                        <div>
                                            <p className="text-xs font-semibold uppercase tracking-widest text-saffron-700">
                                                Why It's Indian
                                            </p>
                                            <p className="mt-0.5 text-sm text-ink leading-relaxed">
                                                {product.why_indian}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="mt-8 prose prose-slate max-w-none">
                        <h2 className="font-heading font-bold text-xl text-ink mb-3">
                            About {product.name}
                        </h2>
                        {product.description.split(/\n+/).map((para, i) => (
                            <p
                                key={i}
                                className="text-base text-ink-muted leading-relaxed mb-4"
                            >
                                {para}
                            </p>
                        ))}
                    </div>

                    {/* Siblings */}
                    {siblings.length > 0 && (
                        <section className="mt-12">
                            <div className="flex items-end justify-between gap-2 mb-4">
                                <h2 className="font-heading font-bold text-2xl text-ink">
                                    More in {product.category_name}
                                </h2>
                                <Link
                                    to={`/category/${product.category_slug}`}
                                    className="text-sm font-semibold text-saffron"
                                >
                                    See all →
                                </Link>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {siblings.map((s) => (
                                    <Link
                                        key={s.id}
                                        to={`/product/${s.slug}`}
                                        className="bg-white border border-line rounded-xl p-4 flex items-center gap-3 card-lift"
                                        data-testid={`sibling-${s.slug}`}
                                    >
                                        <LogoImage
                                            src={s.logo_url}
                                            domain={s.domain}
                                            name={s.name}
                                            size={40}
                                            rounded="rounded-lg"
                                        />
                                        <div className="min-w-0">
                                            <div className="font-semibold text-ink truncate">
                                                {s.name}
                                            </div>
                                            <div className="text-xs text-ink-muted truncate">
                                                {s.domain}
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </section>
                    )}
                </div>

                {/* Sidebar */}
                <aside className="lg:col-span-4 lg:sticky lg:top-24 self-start space-y-4">
                    <a
                        href={product.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl font-semibold bg-indiagreen text-white hover:bg-indiagreen-700 transition-colors"
                        data-testid="sidebar-visit-website"
                    >
                        Visit Website
                        <ArrowUpRight className="h-4 w-4" />
                    </a>

                    <div
                        className="bg-indiagreen-50 border border-indiagreen-200 rounded-2xl p-5"
                        data-testid="verified-indian-card"
                    >
                        <div className="flex items-start gap-2">
                            <span aria-hidden className="text-xl">
                                ✅
                            </span>
                            <div>
                                <p className="font-heading font-bold text-indiagreen-700">
                                    Verified Indian Company
                                </p>
                                <p className="mt-1 text-sm text-ink leading-relaxed">
                                    {product.why_indian ||
                                        "Indian-founded and Indian-headquartered."}
                                </p>
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={handleVote}
                        disabled={hasVoted}
                        className={`w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold border transition-colors ${
                            hasVoted
                                ? "border-indiagreen-200 bg-indiagreen-50 text-indiagreen-700"
                                : "border-line bg-white text-ink hover:border-saffron/40 hover:text-saffron"
                        }`}
                        data-testid="sidebar-upvote"
                    >
                        <ChevronUp className="h-5 w-5" />
                        {hasVoted ? "Upvoted" : "Upvote"} · {count}
                    </button>

                    <div className="bg-white border border-line rounded-2xl p-5">
                        <h3 className="font-heading font-bold text-ink mb-3">
                            Quick facts
                        </h3>
                        <dl className="text-sm space-y-2">
                            <div className="flex justify-between gap-3">
                                <dt className="text-ink-muted">Website</dt>
                                <dd className="text-ink font-medium truncate text-right">
                                    {product.domain}
                                </dd>
                            </div>
                            <div className="flex justify-between gap-3">
                                <dt className="text-ink-muted">Category</dt>
                                <dd className="text-ink font-medium text-right">
                                    {product.category_name}
                                </dd>
                            </div>
                            <div className="flex justify-between gap-3">
                                <dt className="text-ink-muted">Made in</dt>
                                <dd className="text-ink font-medium text-right">
                                    🇮🇳 India
                                </dd>
                            </div>
                            <div className="flex justify-between gap-3">
                                <dt className="text-ink-muted">Upvotes</dt>
                                <dd className="text-ink font-medium text-right">
                                    {count}
                                </dd>
                            </div>
                        </dl>
                    </div>

                    <div className="bg-white border border-line rounded-2xl p-5">
                        <h3 className="font-heading font-bold text-ink mb-3">
                            Share
                        </h3>
                        <ShareButtons product={product} />
                    </div>
                </aside>
            </div>
        </div>
    );
}
